import { useCallback, useEffect, useMemo, useRef } from "react";
import { readDraft, removeDraft, writeDraft } from "../utils/drafts";

// Long enough not to write on every keystroke, short enough that the text is
// stored before anyone can close the app after typing it.
const SAVE_DELAY_MS = 400;

/**
 * Autosave for a text box. Pass the draft's key (null turns it off), call
 * save() with the text as it changes and clear() once it has been sent.
 * Whatever is still waiting is written when the screen goes away, when the key
 * changes, or when the app is sent to the background.
 */
export function useDraft(key) {
  const pendingRef = useRef(null);
  const timerRef = useRef(null);

  const flush = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    const pending = pendingRef.current;
    pendingRef.current = null;
    if (pending) writeDraft(pending.key, pending.draft);
  }, []);

  const save = useCallback(
    (draft) => {
      if (!key) return;
      // The key travels with the text, so a flush that runs after the key has
      // changed still stores the text where it was written.
      pendingRef.current = { key, draft };
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(flush, SAVE_DELAY_MS);
    },
    [key, flush],
  );

  const clear = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    pendingRef.current = null;
    removeDraft(key);
  }, [key]);

  const load = useCallback(() => readDraft(key), [key]);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") flush();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("pagehide", flush);
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("pagehide", flush);
    };
  }, [flush]);

  // Leaving the screen, or moving to another draft, must not lose the last
  // keystrokes still waiting on the timer.
  useEffect(() => flush, [key, flush]);

  return useMemo(
    () => ({ load, save, clear, flush }),
    [load, save, clear, flush],
  );
}
